/**
 * Delete File Mutation Hooks
 *
 * React Query mutation hooks for file deletion operations
 */

'use client';

import {
  useMutation,
  useQueryClient,
  type UseMutationResult
} from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { filesApi } from '../client';
import { DirectApiError, handleMutationError } from '@/lib/api/direct-client';
import type { FileListResponse } from '../types';
import { createFileErrorHandler, shouldRetry } from './helpers';
import { fileKeys, taskKeys, timelineKeys } from '@/lib/query-keys';

// ============================================================================
// DELETE SINGLE FILE
// ============================================================================

/**
 * Hook to delete a file with optimistic updates
 *
 * @returns Mutation object with:
 * - `mutate`: Function to trigger deletion
 * - `isPending`: Loading state
 * - `isError`: Error state
 * - `error`: Error object if any
 * - `isSuccess`: Success state
 *
 * @example
 * ```tsx
 * const deleteMutation = useDeleteFile();
 * deleteMutation.mutate({ matterId: 1, fileId: 123 });
 * ```
 */
export function useDeleteFile(): UseMutationResult<
  void,
  unknown,
  { matterId: number; fileId: number },
  { previousFiles?: FileListResponse; matterId: number; fileId: number }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ matterId, fileId }) => {
      return filesApi.deleteFile(matterId, fileId);
    },

    // Retry configuration
    retry: shouldRetry,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

    // Optimistic update
    onMutate: async ({ matterId, fileId }) => {
      // Cancel any in-flight queries for the files list
      await queryClient.cancelQueries({
        queryKey: fileKeys.forMatter(matterId)
      });

      // Snapshot the previous value
      const previousFiles = queryClient.getQueryData<FileListResponse>(
        fileKeys.forMatter(matterId)
      );

      // Optimistically remove the file from the list
      if (previousFiles) {
        queryClient.setQueryData<FileListResponse>(
          fileKeys.forMatter(matterId),
          {
            ...previousFiles,
            files: previousFiles.files.filter((f) => f.id !== fileId),
            total: previousFiles.total - 1
          }
        );
      }

      // Return context with snapshot
      return { previousFiles, matterId, fileId };
    },

    // Handle error - rollback optimistic update
    onError: (error, variables, context) => {
      // Rollback to the previous value
      if (context?.previousFiles) {
        queryClient.setQueryData(
          fileKeys.forMatter(context.matterId),
          context.previousFiles
        );
      }

      // Show error toast with appropriate message
      if (error instanceof DirectApiError) {
        if (error.status === 500 && error.message?.includes('tablePath')) {
          toast.error(
            'Unable to delete file at this time. This is a known issue being resolved.'
          );
        } else if (error.status === 400) {
          toast.error(
            'This file cannot be deleted because it is attached to a sent correspondence'
          );
        } else if (error.status === 404) {
          toast.error('File not found');
        } else {
          toast.error(handleMutationError(error));
        }
      } else {
        createFileErrorHandler('delete')(error);
      }
    },

    // Always refetch after mutation settles
    onSettled: (data, error, variables) => {
      // Invalidate and refetch files list
      queryClient.invalidateQueries({
        queryKey: fileKeys.forMatter(variables.matterId)
      });

      // Remove the specific file from cache
      queryClient.removeQueries({
        queryKey: fileKeys.item(variables.fileId)
      });

      // Invalidate tasks and history as they depend on document status
      queryClient.invalidateQueries({
        queryKey: taskKeys.dashboard(variables.matterId, 0)
      });
      queryClient.invalidateQueries({
        queryKey: taskKeys.dashboard(variables.matterId, 1)
      });
      queryClient.invalidateQueries({
        queryKey: timelineKeys.forMatter(variables.matterId)
      });
      queryClient.invalidateQueries({
        queryKey: timelineKeys.history(variables.matterId)
      });
    },

    // Success handler
    onSuccess: () => {
      toast.success('Document deleted successfully');
    }
  });
}
